import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from "contexts/authContext";
import { getToken } from "utils/auth";
import { fireSwal } from "utils/swalConfig";
import { FaSchool, FaUserGraduate, FaPlus, FaSpinner } from 'react-icons/fa';
import { HeaderWebEffect } from 'components/card/WebEffect';
import axios from 'axios';

// --- Reusable UI Components ---

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Reusable StatCard for the header
const StatCard = ({ icon, label, value, isLoading }) => (
  <div className="bg-white dark:bg-navy-800 p-4 rounded-xl shadow-md flex items-center gap-4">
    <div className="bg-lightPrimary dark:bg-navy-700 text-brand-500 dark:text-brand-400 rounded-full p-3">
      {icon}
    </div>
    <div>
      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-2xl font-bold text-navy-700 dark:text-white">
          <FaSpinner className="animate-spin" />
        </div>
      ) : (
        <p className="text-2xl font-bold text-navy-700 dark:text-white">{value}</p>
      )}
    </div>
  </div>
);

// Reusable Form Input to reduce boilerplate
const FormInput = ({ label, name, type, value, onChange, required = true }) => (
  <div>
    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-4 py-2 border rounded-lg dark:bg-navy-700 dark:border-navy-600 dark:text-white focus:ring-brand-500 focus:border-brand-500"
    />
  </div>
);

// Reusable Form Select
const FormSelect = ({ label, name, value, onChange, options, required = true }) => (
    <div>
      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{label}</label>
      <select 
        name={name} 
        value={value} 
        onChange={onChange} 
        required={required} 
        className="w-full px-4 py-2 border rounded-lg dark:bg-navy-700 dark:border-navy-600 dark:text-white focus:ring-brand-500 focus:border-brand-500"
      >
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
);

// --- Main Header Component ---

const ClassroomHeader = () => {
  const queryClient = useQueryClient();
  const { user: currentUser, loading: authLoading } = useAuth();
  const token = getToken();

  // State for Modals
  const [isStudentModalOpen, setStudentModalOpen] = useState(false);
  const [isClassroomModalOpen, setClassroomModalOpen] = useState(false);

  // State for fetched data
  const [stats, setStats] = useState({ activeClassrooms: 0, totalStudents: 0, coursesTaught: 0 });

  // State for forms
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', school: '', email: '', password: '',
    confirmPassword: '', username: '', country: 'Kenya', phoneNumber: ''
  });
  const [classroomData, setClassroomData] = useState({
    name: '', grade: '', course: '', mentorId: currentUser?.id || ''
  });

  useEffect(() => {
      if (currentUser?.mentorStats) {
        setStats(currentUser.mentorStats);
      } else {
        setStats({ activeClassrooms: 0, totalStudents: 0, coursesTaught: 0 });
      }
    }, [currentUser]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    // Only allow letters and numbers, max 13 characters
    const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 13);
    setFormData({ ...formData, username: sanitizedValue });
  };

  const handleClassroomChange = (e) => {
    setClassroomData({ ...classroomData, [e.target.name]: e.target.value });
  };

  const handleRegisterStudent = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      fireSwal({ title: 'Error', text: 'Passwords do not match.', icon: 'error', actionType: 'delete' });
      return;
    }
    
    // Validate username format
    if (!formData.username.trim()) {
      fireSwal({ title: 'Error', text: 'Username is required.', icon: 'error', actionType: 'delete' });
      return;
    }
    
    if (formData.username.trim().length < 5) {
      fireSwal({ title: 'Error', text: 'Username must be at least 5 characters long.', icon: 'error', actionType: 'delete' });
      return;
    }
    
    if (formData.username.trim().length > 13) {
      fireSwal({ title: 'Error', text: 'Username must be no more than 13 characters long.', icon: 'error', actionType: 'delete' });
      return;
    }
    
    if (!/^[a-zA-Z0-9]+$/.test(formData.username.trim())) {
      fireSwal({ title: 'Error', text: 'Username must contain only letters and numbers.', icon: 'error', actionType: 'delete' });
      return;
    }
    
    const { confirmPassword, ...sanitizedData } = formData;
    
    // Auto-prefix phone number
    const phonePrefix = sanitizedData.country === 'Kenya' ? '+254' : '+252';
    if (!sanitizedData.phoneNumber.startsWith(phonePrefix)) {
        sanitizedData.phoneNumber = phonePrefix + parseInt(sanitizedData.phoneNumber, 10);
    }

    // If currentUser is mentor and no school provided, set to currentUser.school
    if (currentUser.role === 'mentor' && !sanitizedData.school) {
      sanitizedData.school = currentUser.school;
    }

    try {
      await axios.post(`${API_BASE}/api/auth/register-student`, sanitizedData);
      fireSwal({ title: 'Success!', text: 'Student registered successfully.', icon: 'success', timer: 2000, showConfirmButton: false });
      setStudentModalOpen(false);
      setFormData({ firstName: '', lastName: '', school: '', email: '', password: '', confirmPassword: '', username: '', country: 'Kenya', phoneNumber: '' });
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle specific validation errors
      if (error.response?.data?.field === 'username') {
        fireSwal({ 
          title: 'Invalid Username', 
          text: error.response.data.validation || error.response.data.message, 
          icon: 'error', 
          actionType: 'delete' 
        });
      } else {
        const errorMsg = error.response?.data?.msg || error.response?.data?.message || 'An unknown error occurred.';
        fireSwal({ title: 'Registration Failed', text: errorMsg, icon: 'error', actionType: 'delete' });
      }
    }
  };

  // Function to generate a random classroom code
  const generateClassroomCode = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const prefix = Array(3).fill(0).map(() => letters[Math.floor(Math.random() * letters.length)]).join('');
    const number = Math.floor(100 + Math.random() * 900);
    const suffix = letters[Math.floor(Math.random() * letters.length)];
    return `${prefix}-${number}${suffix}`;
  };

  const handleCreateClassroom = async (e) => {
    e.preventDefault();
    if (!token) {
        fireSwal({ title: 'Authentication Error', text: "You're not authenticated.", icon: 'error', actionType: 'delete' });
        return;
    }

    const newCode = generateClassroomCode();
    const payload = { ...classroomData, code: newCode, mentorId: currentUser.id };

    try {
      await axios.post(`${API_BASE}/api/classrooms`, payload, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      
      setClassroomModalOpen(false);
      await fireSwal({ 
        title: 'Success!', 
        text: `Your new classroom '${payload.name}' has been created with code '${newCode}'.`, 
        icon: 'success', 
        timer: 3500,
        showConfirmButton: false 
      });

      // Invalidate mentorClassrooms query
      queryClient.invalidateQueries({ queryKey: ['mentorClassrooms'] });

      // Dispatch custom event for backward compatibility
      window.dispatchEvent(new Event('classroomCreated'));
      
      // Reset form data
      setClassroomData({ name: '', grade: '', course: '', mentorId: currentUser?.id || '' });

    } catch (error) {
      console.error('Classroom creation error:', error);
      const errorMsg = error.response?.data?.message || 'An unknown error occurred.';
      fireSwal({ title: 'Creation Failed', text: errorMsg, icon: 'error', actionType: 'delete' });
    }
  };

  const getTimeOfDay = () => {
    const EATTime = new Date(new Date().toLocaleString("en-US", { timeZone: "Africa/Nairobi" }));
    const hours = EATTime.getHours();
    if (hours < 12) return 'Good Morning';
    if (hours < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const countryOptions = [
      { value: 'Kenya', label: 'Kenya (+254)'},
      { value: 'Somalia', label: 'Somalia (+252)'}
  ];

  return (
    <>
      {/* Container for the header content with decorative SVG */}
      <div className="relative bg-white dark:bg-navy-900 p-6 rounded-2xl shadow-xl overflow-hidden my-4">
      <HeaderWebEffect />
        {/* Main Content (placed above the SVG) */}
        <div className="relative z-10 space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
            <div>
                <h1 className="text-4xl font-bold text-navy-700 dark:text-white">{getTimeOfDay()}, {currentUser?.firstName || 'Mentor'}!</h1>
                <p className="mt-1 text-lg text-gray-500 dark:text-gray-300">Welcome Mentor! Here's what's happening.</p>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
                <button onClick={() => setStudentModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-navy-700 border border-gray-300 dark:border-navy-600 rounded-lg font-semibold text-navy-700 dark:text-white hover:bg-gray-100 dark:hover:bg-navy-600 transition-colors">
                <FaUserGraduate /><span>New Student</span>
                </button>
                <button onClick={() => setClassroomModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg font-semibold hover:bg-brand-600 transition-colors">
                <FaPlus /><span>New Classroom</span>
                </button>
            </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <StatCard icon={<FaSchool size={24} />} label="Active Classrooms" value={stats.activeClassrooms} isLoading={authLoading} />
            <StatCard icon={<FaUserGraduate size={24} />} label="Total Students" value={stats.totalStudents} isLoading={authLoading} />
            <StatCard icon={<FaSchool size={24} />} label="Courses Taught" value={stats.coursesTaught} isLoading={authLoading} />
            </div>
        </div>
      </div>

      {/* Modals are rendered here, at the top level, to avoid stacking context issues */}
      {isStudentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-navy-800 p-6 rounded-2xl shadow-2xl w-[90%] max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-navy-700 dark:text-white">Register New Student</h2>
            <form onSubmit={handleRegisterStudent} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2"><FormInput label="First Name" name="firstName" type="text" value={formData.firstName} onChange={handleInputChange} /></div>
                <div className="w-full sm:w-1/2"><FormInput label="Last Name" name="lastName" type="text" value={formData.lastName} onChange={handleInputChange} /></div>
              </div>
              <FormInput 
                label="Username" 
                name="username" 
                type="text" 
                value={formData.username} 
                onChange={handleUsernameChange}
                placeholder="5-13 characters, letters and numbers only"
                maxLength={13}
              />
              <FormInput label="School" name="school" type="text" value={formData.school} onChange={handleInputChange} />
              <FormInput label="Email" name="email" type="email" value={formData.email} onChange={handleInputChange} />
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2"><FormInput label="Password" name="password" type="password" value={formData.password} onChange={handleInputChange} /></div>
                <div className="w-full sm:w-1/2"><FormInput label="Confirm Password" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleInputChange} /></div>
              </div>
              <FormSelect label="Country" name="country" value={formData.country} onChange={handleInputChange} options={countryOptions} />
              <FormInput label="Phone Number" name="phoneNumber" type="tel" value={formData.phoneNumber} onChange={handleInputChange} />

              <div className="flex justify-end space-x-4 pt-6">
                <button type="button" onClick={() => setStudentModalOpen(false)} className="px-5 py-2.5 bg-gray-200 dark:bg-navy-700 text-black dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-navy-600 font-semibold">Cancel</button>
                <button type="submit" className="px-5 py-2.5 bg-brand-500 text-white rounded-lg font-semibold hover:bg-brand-600">Register</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isClassroomModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-navy-800 p-6 rounded-2xl shadow-2xl w-[90%] max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-navy-700 dark:text-white">Create New Classroom</h2>
            <form onSubmit={handleCreateClassroom} className="space-y-4">
              <FormInput label="Classroom Name" name="name" type="text" value={classroomData.name} onChange={handleClassroomChange} />
              <FormInput label="Grade" name="grade" type="text" value={classroomData.grade} onChange={handleClassroomChange} />
              <FormInput label="Course" name="course" type="text" value={classroomData.course} onChange={handleClassroomChange} />
              <div className="flex justify-end space-x-4 pt-6">
                <button type="button" onClick={() => setClassroomModalOpen(false)} className="px-5 py-2.5 bg-gray-200 dark:bg-navy-700 text-black dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-navy-600 font-semibold">Cancel</button>
                <button type="submit" className="px-5 py-2.5 bg-brand-500 text-white rounded-lg font-semibold hover:bg-brand-600">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ClassroomHeader;